import { useCallback, useEffect, useState } from "react"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import {
  CAMPAIGN_SCRIPT_API_PATH,
  CAMPAIGN_SCRIPT_APPROVE_API_PATH,
  SCRIPT_STATUS,
} from "./campaignScriptConstants"

const EMPTY_SCRIPT = {
  exists: false,
  content: "",
  status: SCRIPT_STATUS.awaitingApproval,
  updated_at: null,
  updated_by: null,
  approved_at: null,
  approved_by: null,
  revisions: [],
  approvals: [],
  can_approve: false,
}

export function useCampaignScript() {
  const axios = useAuthorizedAxiosClient()
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isApproving, setIsApproving] = useState(false)
  const [script, setScript] = useState(EMPTY_SCRIPT)

  const applyPayload = useCallback((payload) => {
    if (!payload || typeof payload !== "object") {
      setScript(EMPTY_SCRIPT)
      return EMPTY_SCRIPT
    }
    const next = {
      ...EMPTY_SCRIPT,
      ...payload,
      content: payload.content || "",
      revisions: Array.isArray(payload.revisions) ? payload.revisions : [],
      approvals: Array.isArray(payload.approvals) ? payload.approvals : [],
    }
    setScript(next)
    return next
  }, [])

  const loadScript = useCallback(() => {
    setIsLoading(true)
    return axios
      .get(CAMPAIGN_SCRIPT_API_PATH)
      .then((res) => applyPayload(res.data))
      .catch(() => applyPayload(EMPTY_SCRIPT))
      .finally(() => setIsLoading(false))
  }, [axios, applyPayload])

  useEffect(() => {
    loadScript()
  }, [loadScript])

  const saveDraft = useCallback(
    (content) => {
      setIsSaving(true)
      return axios
        .put(CAMPAIGN_SCRIPT_API_PATH, { content })
        .then((res) => applyPayload(res.data))
        .finally(() => setIsSaving(false))
    },
    [axios, applyPayload]
  )

  const approveScript = useCallback(
    (content) => {
      setIsApproving(true)
      return axios
        .post(CAMPAIGN_SCRIPT_APPROVE_API_PATH, { content })
        .then((res) => applyPayload(res.data))
        .finally(() => setIsApproving(false))
    },
    [axios, applyPayload]
  )

  return {
    isLoading,
    isSaving,
    isApproving,
    script,
    loadScript,
    saveDraft,
    approveScript,
  }
}
